import { buildFormSelector } from '../utils';

/**
 * @description This class is not intended to have a separate DOM representation.
 *
 * @param AjaxForm Base widget for extending
 * @returns AjaxFormWithFriendlyCaptcha Form widget
 */
function AjaxFormWithFriendlyCaptchaClassCreator(AjaxForm: import('widgets/forms/AjaxForm').TAjaxForm) {
    /**
     * @category widgets
     * @subcategory forms
     * @class AjaxFormWithFriendlyCaptcha
     * @augments AjaxForm
     * @classdesc Represents AjaxFormWithFriendlyCaptcha component with next features:
     * 1. Allows to process FriendlyCaptcha on form submit
     * 2. Allows to reset FriendlyCaptcha after submission
     */
    class AjaxFormWithFriendlyCaptcha extends AjaxForm {
        prefs() {
            return {
                friendlyCaptchaEnabled: false,
                ...super.prefs()
            };
        }

        /**
         * @description Handles submit Form
         * @returns Promise object represents server response
         */
        handleSubmit() {
            if (this.prefs().friendlyCaptchaEnabled) {
                const currentForm = this.ref('self').get() as HTMLFormElement;
                const solution = currentForm?.querySelector('.frc-captcha-solution') as HTMLInputElement;
                const solutionField = currentForm?.querySelector<HTMLInputElement>(`.m-captcha-solution input`) as HTMLInputElement;

                if (solutionField) {
                    solutionField.value = solution?.value || '';
                }
            }

            return super.handleSubmit();
        }

        /**
         * @description Handle server response
         * @param data - response data
         * @param [data.success] - response status
         */
        onSubmitted(data: TServerResponse): void {
            if (this.prefs().friendlyCaptchaEnabled) {
                const form = this.ref('self').get() as HTMLFormElement;

                if (!data?.success) {
                    const selector = buildFormSelector(form, false);

                    this.eventBus().emit('captcha.reset.on.error', selector);
                }

                const captchaFormInput = form?.querySelector<HTMLInputElement>(`.m-captcha-solution input`) as HTMLInputElement;

                if (captchaFormInput) {
                    captchaFormInput.value = '';
                }
            }

            super.onSubmitted(data);
        }
    }

    return AjaxFormWithFriendlyCaptcha;
}

export type TAjaxFormWithFriendlyCaptcha = ReturnType<typeof AjaxFormWithFriendlyCaptchaClassCreator>;

export type TAjaxFormWithFriendlyCaptchaInstance = InstanceType<TAjaxFormWithFriendlyCaptcha>;

export default AjaxFormWithFriendlyCaptchaClassCreator;
